import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Breadcrumbs, Card, CardActionArea, CardContent, Link, Rating, Stack, Typography } from '@mui/material';
import AlbumIcon from '@mui/icons-material/Album';
import FolderIcon from '@mui/icons-material/Folder';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { MusicAlbumRow, formatBytes, formatDuration } from '../../api/music';
import { NO_ALBUM } from './AlbumTree';

interface AlbumCardsProps {
    /** Rows from GET music/albums?include_parents=1. */
    rows: MusicAlbumRow[];
    /** null = root, NO_ALBUM = tracks without album_path, otherwise an album_path. */
    selected: string | null;
    onSelect: (value: string | null) => void;
}

interface CardData {
    path: string;
    name: string;
    trackCount: number;
    size: number;
    duration: number;
    avgRating: number | null;
    ratedCount: number;
    subAlbums: number;
}

const toCard = (row: MusicAlbumRow, subAlbums: number): CardData => ({
    path: row.album_path ?? NO_ALBUM,
    name: row.album || (row.album_path ?? '').split('/').pop() || '',
    trackCount: row.track_count_recursive ?? row.track_count,
    size: row.total_size_bytes_recursive ?? row.total_size_bytes,
    duration: row.total_duration_seconds_recursive ?? row.total_duration_seconds ?? 0,
    avgRating: row.avg_rating_recursive ?? row.avg_rating ?? null,
    ratedCount: row.rated_count_recursive ?? row.rated_count ?? 0,
    subAlbums,
});

/** Grid of album cards for one level of the album_path hierarchy, with a breadcrumb to navigate up. */
export const AlbumCards = ({ rows, selected, onSelect }: AlbumCardsProps) => {
    const { t } = useTranslation();
    const current = selected === NO_ALBUM ? null : selected;

    const cards = useMemo(() => {
        const withPath = rows.filter((row) => row.album_path);
        const childCount = new Map<string, number>();
        for (const row of withPath) {
            if (row.parent_path) childCount.set(row.parent_path, (childCount.get(row.parent_path) ?? 0) + 1);
        }
        const level = withPath
            .filter((row) => (row.parent_path ?? null) === current)
            .map((row) => toCard(row, childCount.get(row.album_path as string) ?? 0));
        if (current === null) {
            const orphans = rows.filter((row) => !row.album_path);
            if (orphans.length > 0) {
                level.push({
                    path: NO_ALBUM,
                    name: t('music.noAlbum'),
                    trackCount: orphans.reduce((sum, row) => sum + row.track_count, 0),
                    size: orphans.reduce((sum, row) => sum + row.total_size_bytes, 0),
                    duration: orphans.reduce((sum, row) => sum + (row.total_duration_seconds ?? 0), 0),
                    avgRating: null,
                    ratedCount: 0,
                    subAlbums: 0,
                });
            }
        }
        return level.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));
    }, [current, rows, t]);

    const crumbs = useMemo(() => {
        if (!current) return [];
        const parts = current.split('/');
        return parts.map((name, index) => ({ name, path: parts.slice(0, index + 1).join('/') }));
    }, [current]);

    return (
        <Box sx={{ mb: 2 }}>
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 1.5 }}>
                {selected === null && crumbs.length === 0 ? (
                    <Typography color="text.primary">{t('music.albums')}</Typography>
                ) : (
                    <Link component="button" type="button" underline="hover" color="inherit" onClick={() => onSelect(null)}>
                        {t('music.albums')}
                    </Link>
                )}
                {selected === NO_ALBUM && <Typography color="text.primary">{t('music.noAlbum')}</Typography>}
                {crumbs.map((crumb, index) => (index === crumbs.length - 1 ? (
                    <Typography key={crumb.path} color="text.primary">{crumb.name}</Typography>
                ) : (
                    <Link key={crumb.path} component="button" type="button" underline="hover" color="inherit" onClick={() => onSelect(crumb.path)}>
                        {crumb.name}
                    </Link>
                )))}
            </Breadcrumbs>

            {cards.length === 0 ? (
                selected === null && <Typography color="text.secondary">{t('music.cards.empty')}</Typography>
            ) : (
                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
                    {cards.map((card) => (
                        <Card key={card.path} variant="outlined">
                            <CardActionArea onClick={() => onSelect(card.path)} sx={{ height: '100%' }}>
                                <Box sx={{ height: 72, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'action.hover', color: 'text.secondary' }}>
                                    {card.subAlbums > 0 ? <FolderIcon sx={{ fontSize: 40 }} /> : <AlbumIcon sx={{ fontSize: 40 }} />}
                                </Box>
                                <CardContent>
                                    <Typography variant="subtitle1" noWrap title={card.path === NO_ALBUM ? card.name : card.path}>{card.name}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('music.cards.tracks', { count: card.trackCount })}
                                        {card.subAlbums > 0 && ` · ${t('music.cards.subAlbums', { count: card.subAlbums })}`}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                        {formatDuration(card.duration)} · {formatBytes(card.size)}
                                    </Typography>
                                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }} title={t('music.cards.ratedOf', { rated: card.ratedCount, count: card.trackCount })}>
                                        <Rating value={card.avgRating ?? 0} precision={0.1} max={5} size="small" readOnly />
                                        <Typography variant="caption" color="text.secondary">
                                            {card.avgRating !== null ? card.avgRating.toFixed(1) : t('music.cards.unrated')}
                                        </Typography>
                                    </Stack>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    ))}
                </Box>
            )}
        </Box>
    );
};
